import { useCallback } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { isAxiosError } from "axios"
import { generateDummyData, getDummyData } from "api/Repos/dummy-data"
import { datafileGet, datafileText } from "api/Repos/data-import"
import { queryKeys } from "api/queryKeys"
import { useAuthedAxios } from "hooks/core/useAuthedAxios"
import { useProjectTypeQuery } from "features/Projects/api/useProjectsQuery"
import { DATAFILE_TYPE } from "types/dataImport.types"
import { DummyDataState, DummyGenerateRequest } from "types/dummyData.types"
import { PROJECT_TYPE } from "types/project.types"
import { mapIdVariables } from "../model/mapVariables"

/** Query key of a project's dummy data state: `["dummy-data", projectId]`. */
export const dummyDataKey = (projectId: string) =>
  queryKeys.dummyData.project(projectId)

/**
 * True for the 409 the Conductor answers when real data exists and `replace`
 * is not set. `generateDummyData` turns every other 409 (a run already in
 * progress, real data arriving during the run) into a plain Error.
 */
export const isConflict = (e: unknown): boolean =>
  isAxiosError(e) && e.response?.status === 409

/**
 * The project's dummy data state (profile, marker, last run) and the
 * generate command. Generating refetches the state whether it worked or not
 * (the Conductor saves the profile before it generates), and the project's
 * file-presence probes (it may have written the map and the data file).
 * `readFresh` reads the state from the server whatever the cache holds, for
 * an action that must not trust a marker read earlier; with `{ csv: true }`
 * it also asks whether the CSV is still the run's (`csvIsDummy`), which the
 * ordinary state read leaves out as the server must hash the whole CSV.
 */
export const useDummyData = (projectId?: string) => {
  const getAxios = useAuthedAxios()
  const queryClient = useQueryClient()
  const state = useQuery({
    queryKey: dummyDataKey(projectId ?? ""),
    queryFn: async () => getDummyData(await getAxios(), projectId as string),
    enabled: !!projectId,
    staleTime: 30_000,
    retry: false,
    meta: { silent: true },
  })
  const mutation = useMutation({
    mutationFn: async (request: DummyGenerateRequest) =>
      generateDummyData(await getAxios(), projectId as string, request),
    onSettled: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: dummyDataKey(projectId ?? "") }),
        queryClient.invalidateQueries({
          queryKey: queryKeys.datafileExists.project(projectId ?? ""),
        }),
      ]),
  })
  // Real data imported elsewhere turns the marker off: reads the state again,
  // joining a read already on its way rather than cancelling it.
  const refresh = useCallback(() => {
    if (!projectId) return
    void queryClient.invalidateQueries(
      { queryKey: dummyDataKey(projectId) },
      { cancelRefetch: false }
    )
  }, [queryClient, projectId])
  const readFresh = useCallback(
    async (options?: { csv?: boolean }): Promise<DummyDataState> => {
      const fresh = await getDummyData(
        await getAxios(),
        projectId as string,
        options
      )
      queryClient.setQueryData(dummyDataKey(projectId ?? ""), fresh)
      return fresh
    },
    [getAxios, queryClient, projectId]
  )
  const { refetch } = state
  const retry = useCallback(() => void refetch(), [refetch])
  return {
    state: state.data,
    isActive: !!state.data?.active,
    isLoading: state.isLoading,
    /** The state could not be read (and none was read before). */
    isError: state.isError && state.data === undefined,
    retry,
    generate: mutation.mutateAsync,
    isGenerating: mutation.isPending,
    refresh,
    readFresh,
  }
}

/**
 * The project to read dummy data for: its id once the project's type is
 * known to have a questionnaire, undefined otherwise. Data-only and dashboard
 * projects have no questionnaire, so they never have dummy data and their
 * state is never read. Uses the project-type query the project page reads
 * (cached), for screens that are not told the type.
 */
export const useQuestionnaireProjectId = (
  projectId?: string
): string | undefined => {
  const type = useProjectTypeQuery(projectId ?? "", !!projectId)
  const known = (type.data ?? "").toLowerCase()
  if (!projectId || type.data === undefined) return undefined
  return known === PROJECT_TYPE.DATA_ONLY || known === PROJECT_TYPE.DASHBOARD
    ? undefined
    : projectId
}

const DATAFILE_SUBTYPES = {
  dqd: DATAFILE_TYPE.DQD,
  map: DATAFILE_TYPE.MAP,
} as const

/**
 * Whether the project has a data file (`dqd`) or a map (`map`). Uses the
 * size probe: the server answers a number when the file exists.
 * `has` is undefined until the first answer.
 */
export const useProjectHasFile = (
  projectId: string | undefined,
  owner: string | undefined,
  subtype: "dqd" | "map"
) => {
  const getAxios = useAuthedAxios()
  const query = useQuery({
    queryKey: queryKeys.datafileExists.file(projectId ?? "", subtype),
    queryFn: async () => {
      const res = await datafileGet(
        await getAxios(),
        projectId as string,
        owner ?? "",
        DATAFILE_SUBTYPES[subtype],
        "size"
      )
      return typeof res.data === "number"
    },
    enabled: !!projectId,
    retry: false,
    meta: { silent: true },
  })
  const { refetch } = query
  const refresh = useCallback(() => void refetch(), [refetch])
  return { has: query.data, refresh }
}

/**
 * The variables of the project's map that can hold a respondent ID (its INT
 * and TEXT rows), read from the map file while `enabled` (the project has a
 * map). Keyed under the map's file probe, so reading the files again (after
 * an import, a generate or a new map) reads the map again.
 */
export const useMapIdVariables = (projectId: string, enabled: boolean) => {
  const getAxios = useAuthedAxios()
  const query = useQuery({
    queryKey: [...queryKeys.datafileExists.file(projectId, "map"), "id-variables"],
    queryFn: async () =>
      mapIdVariables(await datafileText(await getAxios(), projectId, "map")),
    enabled: enabled && !!projectId,
    retry: false,
    meta: { silent: true },
  })
  return { variables: query.data, isLoading: query.isLoading }
}
